import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCcw, Search, Shield } from "lucide-react";
import { listAllReportExecutions } from "@/lib/portal-reports/admin.functions";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute("/_authenticated/app/admin/portal-reports/")({ component: PortalReportsOps });

type Status = "all" | "succeeded" | "failed" | "running";

function PortalReportsOps() {
  const fn = useServerFn(listAllReportExecutions);
  const [status, setStatus] = useState<Status>("all");
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["portal-reports-ops", status],
    queryFn: () => runServerFn(() => fn({ data: { status, limit: 200 } }), "Loading executions"),
  });

  const filtered = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r: any) =>
      r.report_key.toLowerCase().includes(s) ||
      (r.customer_name ?? "").toLowerCase().includes(s) ||
      (r.error ?? "").toLowerCase().includes(s),
    );
  }, [data, search]);

  const stats = data?.stats ?? { total: 0, failed: 0, succeeded: 0, running: 0, avgMs: 0 };

  return (
    <>
      <PageHeader
        title="Portal Reports — Operations"
        description="Recent customer report runs across the tenant. Use this to spot failing subscriptions."
        actions={
          <div className="flex gap-2">
            <Link to="/app/admin/portal-reports/access">
              <Button variant="outline" size="sm">
                <Shield className="h-4 w-4 mr-1" /> Access control
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCcw className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
          {[
            { label: "Total", value: stats.total },
            { label: "Succeeded", value: stats.succeeded },
            { label: "Failed", value: stats.failed },
            { label: "Running", value: stats.running },
            { label: "Avg duration", value: `${stats.avgMs}ms` },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
                <div className="text-xl font-semibold tabular-nums mt-1">{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="succeeded">Succeeded</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search report, customer, error…"
              className="h-8 pl-7 w-72 text-xs"
            />
          </div>
          <span className="text-xs text-muted-foreground ml-auto tabular-nums">{filtered.length} rows</span>
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
            {!isLoading && filtered.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">No executions match.</div>
            )}
            {filtered.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Started</th>
                      <th className="px-3 py-2 font-medium">Report</th>
                      <th className="px-3 py-2 font-medium">Customer</th>
                      <th className="px-3 py-2 font-medium">Trigger</th>
                      <th className="px-3 py-2 font-medium">Format</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium tabular-nums text-right">Rows</th>
                      <th className="px-3 py-2 font-medium tabular-nums text-right">Duration</th>
                      <th className="px-3 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filtered.map((r: any) => (
                      <tr key={r.id} className={r.status === "failed" ? "bg-destructive/5" : undefined}>
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground tabular-nums">
                          {new Date(r.started_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 font-medium">{r.report_key}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.customer_name ?? "—"}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px]">{r.trigger ?? "manual"}</Badge>
                          {r.cache_hit && <Badge variant="secondary" className="text-[10px] ml-1">cached</Badge>}
                        </td>
                        <td className="px-3 py-2 uppercase text-muted-foreground">{r.format ?? "—"}</td>
                        <td className="px-3 py-2">
                          <Badge
                            variant={r.status === "succeeded" ? "secondary" : r.status === "failed" ? "destructive" : "outline"}
                            className="text-[10px]"
                          >
                            {r.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.row_count ?? "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {r.duration_ms ? `${r.duration_ms}ms` : "—"}
                        </td>
                        <td className="px-3 py-2 text-destructive max-w-xs truncate" title={r.error ?? ""}>{r.error ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}